import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { completeCustomerOnboardingStepOne, getCustomerOnboardingStatus } from '../lib/api';
import { toast } from 'sonner';
import { ArrowRight, CheckCircle, KeyRound, Loader2, Lock, Mail, Phone, ShieldCheck, UserCircle2 } from 'lucide-react';

const MIN_PASSWORD_LENGTH = 6;
const LEGAL_VERSION = '2026-03-29';

const normalizeText = (value) => String(value ?? '').trim();

const getStepOneValidationError = (form) => {
  if (!normalizeText(form.first_name)) return 'First name is required.';
  if (!normalizeText(form.last_name)) return 'Last name is required.';
  if (!normalizeText(form.phone)) return 'Phone number is required.';
  if (!form.password) return 'Password is required.';
  if (form.password.length < MIN_PASSWORD_LENGTH) return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  if (!form.confirm_password) return 'Please confirm your password.';
  if (form.confirm_password.length < MIN_PASSWORD_LENGTH) return `Confirm password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  if (form.password !== form.confirm_password) return 'Passwords do not match.';
  return null;
};

const getOnboardingDraftStorageKey = (token) => `customer_onboarding_draft:${token}`;

const readOnboardingDraft = (token) => {
  if (!token) return null;
  try {
    const raw = localStorage.getItem(getOnboardingDraftStorageKey(token));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed ? parsed : null;
  } catch {
    return null;
  }
};

const writeOnboardingDraft = (token, draft) => {
  if (!token || !draft) return;
  try {
    localStorage.setItem(getOnboardingDraftStorageKey(token), JSON.stringify(draft));
  } catch {
    // best-effort cache only
  }
};

const clearOnboardingDraft = (token) => {
  if (!token) return;
  try {
    localStorage.removeItem(getOnboardingDraftStorageKey(token));
  } catch {
    // ignore cache cleanup failures
  }
};

const extractPrefillData = (payload = {}) => {
  const customer = payload.customer || payload.user || payload.profile || {};
  return {
    email: payload.email || customer.email || '',
    first_name: payload.first_name || customer.first_name || customer.given_name || '',
    last_name: payload.last_name || customer.last_name || customer.family_name || '',
    phone: payload.phone || customer.phone || '',
    company_name: payload.company_name || payload.business_name || customer.company_name || customer.business_name || '',
    business_name: payload.business_name || payload.company_name || customer.business_name || customer.company_name || '',
  };
};

export function CustomerOnboardingPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [loadingSession, setLoadingSession] = useState(true);
  const [submittingStepOne, setSubmittingStepOne] = useState(false);
  const [session, setSession] = useState(null);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [form, setForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    company_name: '',
    business_name: '',
    password: '',
    confirm_password: '',
  });
  const canSubmitStepOne = useMemo(() => !getStepOneValidationError(form), [form]);

  useEffect(() => {
    if (!token) {
      setLoadingSession(false);
      return;
    }

    const bootstrap = async () => {
      try {
        const response = await getCustomerOnboardingStatus({ token });
        const payload = response?.data || {};
        setSession(payload);
        const serverPrefill = extractPrefillData(payload);
        const cachedDraft = readOnboardingDraft(token) || {};
        setForm((prev) => ({
          ...prev,
          email: serverPrefill.email || cachedDraft.email || '',
          first_name: serverPrefill.first_name || cachedDraft.first_name || '',
          last_name: serverPrefill.last_name || cachedDraft.last_name || '',
          phone: serverPrefill.phone || cachedDraft.phone || '',
          company_name: serverPrefill.company_name || cachedDraft.company_name || '',
          business_name: serverPrefill.business_name || cachedDraft.business_name || '',
        }));
      } catch (error) {
        const cachedDraft = readOnboardingDraft(token);
        if (cachedDraft) {
          setForm((prev) => ({
            ...prev,
            email: cachedDraft.email || prev.email,
            first_name: cachedDraft.first_name || prev.first_name,
            last_name: cachedDraft.last_name || prev.last_name,
            phone: cachedDraft.phone || prev.phone,
            company_name: cachedDraft.company_name || prev.company_name,
            business_name: cachedDraft.business_name || prev.business_name,
          }));
        }
        toast.error(error.response?.data?.detail || 'This onboarding link is invalid or expired.');
      } finally {
        setLoadingSession(false);
      }
    };

    bootstrap();
  }, [token]);

  const handleFieldChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleStepOneSubmit = async (event) => {
    event.preventDefault();
    if (!token) {
      toast.error('Missing onboarding token.');
      return;
    }
    const validationError = getStepOneValidationError(form);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    if (!legalAccepted) {
      toast.error('Please accept the Terms and Privacy Policy before proceeding.');
      return;
    }

    try {
      setSubmittingStepOne(true);
      writeOnboardingDraft(token, {
        email: form.email,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        company_name: (form.company_name || form.business_name).trim(),
        business_name: (form.business_name || form.company_name).trim(),
      });
      const response = await completeCustomerOnboardingStepOne({
        token,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        company_name: (form.company_name || form.business_name).trim(),
        password: form.password,
        legal_acceptance: {
          accepted: true,
          accepted_at: new Date().toISOString(),
          terms_version: LEGAL_VERSION,
          sla_version: LEGAL_VERSION,
          refund_policy_version: LEGAL_VERSION,
          acceptance_source: 'customer_onboarding_step_one',
        },
      });
      const status = String(response?.data?.status || '').toLowerCase();
      if (['completed', 'active'].includes(status)) {
        toast.success('Account setup complete.');
        clearOnboardingDraft(token);
      } else {
        toast.success('Setup submitted. Verifying completion...');
      }
      navigate(`/customer/onboarding/success?token=${encodeURIComponent(token)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to complete account setup.');
    } finally {
      setSubmittingStepOne(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Missing Onboarding Link</h2>
            <p className="text-slate-600 mb-6">Your invite link is incomplete. Please use the full email link.</p>
            <Button asChild className="w-full">
              <Link to="/login">Back to Login</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loadingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="p-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-slate-600">Loading your onboarding details...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (['completed', 'active'].includes(String(session?.status || '').toLowerCase())) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-lg border-0 shadow-2xl text-center">
          <CardContent className="p-10">
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="h-9 w-9 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Your account is already active</h2>
            <p className="text-slate-600 mb-7">
              This onboarding link has already been completed. You can continue to login.
            </p>
            <Button onClick={() => navigate('/login')} className="bg-blue-600 hover:bg-blue-700">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 p-4 sm:p-6">
      <div className="max-w-5xl mx-auto py-6 sm:py-12">
        <div className="mb-8 text-center">
          <Badge className="bg-blue-100 text-blue-700 mb-4">Customer Setup</Badge>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">Complete your account setup</h1>
          <p className="text-slate-600">
            Confirm your details, create your password, and accept legal terms to activate your account.
          </p>
        </div>

        <Card className="border-0 shadow-2xl">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <UserCircle2 className="h-6 w-6 text-blue-600" />
              Set password and confirm details
            </CardTitle>
            <CardDescription>
              Complete account setup in one step. Email is locked for security and invite matching.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleStepOneSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input id="first_name" value={form.first_name} onChange={handleFieldChange('first_name')} placeholder="John" data-testid="onboarding-first-name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input id="last_name" value={form.last_name} onChange={handleFieldChange('last_name')} placeholder="Smith" data-testid="onboarding-last-name" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email (cannot be changed)</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="email" value={form.email} disabled className="pl-10 bg-slate-100 text-slate-600" data-testid="onboarding-email-readonly" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="phone" value={form.phone} onChange={handleFieldChange('phone')} className="pl-10" placeholder="+61..." data-testid="onboarding-phone" />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_name">Business Name</Label>
                <Input id="company_name" value={form.company_name || form.business_name} onChange={handleFieldChange('company_name')} placeholder="Acme Services" data-testid="onboarding-company-name" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Create Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="password" type="password" value={form.password} onChange={handleFieldChange('password')} className="pl-10" placeholder={`Minimum ${MIN_PASSWORD_LENGTH} characters`} data-testid="onboarding-password" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Confirm Password</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="confirm_password" type="password" value={form.confirm_password} onChange={handleFieldChange('confirm_password')} className="pl-10" placeholder="Re-enter password" data-testid="onboarding-confirm-password" />
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-700">
                <div className="flex items-start gap-2">
                  <ShieldCheck className="h-4 w-4 mt-0.5" />
                  <p>This invite is secure and single-customer specific. Your email address is fixed and cannot be edited.</p>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-start gap-3">
                  <Checkbox id="onboarding-legal-acceptance" checked={legalAccepted} onCheckedChange={(checked) => setLegalAccepted(checked === true)} className="mt-1" />
                  <div className="text-sm text-slate-700">
                    <Label htmlFor="onboarding-legal-acceptance" className="font-medium cursor-pointer">
                      I agree to the legal terms
                    </Label>
                    <p className="mt-1">
                      I accept the{' '}
                      <a href="/legal" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Terms and Conditions</a>{' '}
                      and{' '}
                      <a href="/privacy-policy" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Privacy Policy</a>.
                    </p>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
                disabled={!canSubmitStepOne || !legalAccepted || submittingStepOne}
                data-testid="onboarding-step-one-submit"
              >
                {submittingStepOne ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Complete Setup
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function CustomerOnboardingSuccessPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const rawSessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [attempt, setAttempt] = useState(0);
  const navigate = useNavigate();

  const decodeSafe = (value) => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };
  const isSessionPlaceholder = (value) => {
    const normalized = String(value || '').trim();
    if (!normalized) return true;
    const decoded = decodeSafe(normalized);
    return /^\{.*\}$/.test(decoded) || decoded.includes('CHECKOUT_SESSION_ID');
  };
  const sessionId = isSessionPlaceholder(rawSessionId) ? null : rawSessionId;

  useEffect(() => {
    if (!token && !sessionId) {
      setStatus('unknown');
      return;
    }

    let cancelled = false;
    let timeoutId = null;
    const pollIntervalMs = 2000;
    const maxAttempts = 8;

    const poll = async (nextAttempt) => {
      if (cancelled) return;
      setAttempt(nextAttempt);
      try {
        const response = await getCustomerOnboardingStatus({ token, session_id: sessionId });
        const onboardingStatus = String(response?.data?.status || '').toLowerCase();
        if (['completed', 'active'].includes(onboardingStatus)) {
          setStatus('success');
          return;
        }
        if (['failed', 'cancelled', 'canceled', 'expired'].includes(onboardingStatus)) {
          setStatus('failed');
          return;
        }
      } catch {
        // continue polling
      }

      if (nextAttempt >= maxAttempts) {
        setStatus('pending');
        return;
      }
      timeoutId = setTimeout(() => poll(nextAttempt + 1), pollIntervalMs);
    };

    setStatus('loading');
    poll(1);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [token, sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-xl border-0 shadow-2xl">
        <CardContent className="p-10 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="h-10 w-10 animate-spin text-blue-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Verifying setup</h2>
              <p className="text-slate-600">Please wait while we confirm your onboarding activation.</p>
              <p className="text-xs text-slate-400 mt-2">Checking attempt {attempt}...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="h-9 w-9 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-3">Account setup complete</h2>
              <p className="text-slate-600 mb-7">
                Your customer account is now active. You can continue to login or open your dashboard.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-3">
                <Button onClick={() => navigate('/login')} className="bg-blue-600 hover:bg-blue-700 h-11">
                  Go to Login
                </Button>
                <Button variant="outline" onClick={() => navigate('/dashboard')} className="h-11">
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}

          {status !== 'loading' && status !== 'success' && (
            <>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">
                {status === 'failed' ? 'Activation failed' : 'Activation is still processing'}
              </h2>
              <p className="text-slate-600 mb-7">
                {status === 'failed'
                  ? 'Please retry onboarding setup or contact support.'
                  : 'We have your setup request and are waiting for final confirmation from onboarding.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button variant="outline" onClick={() => window.location.reload()}>
                  Refresh Status
                </Button>
                {token && (
                  <Button variant="outline" onClick={() => navigate(`/customer/onboarding?token=${encodeURIComponent(token)}`)}>
                    Back to Onboarding
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
